import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import Key from '@mui/icons-material/Key';
import {
  DataGridPremium,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  gridDimensionsSelector,
  useGridApiContext,
  useGridSelector,
  type GridColDef,
  type GridRowId,
  type GridRowParams,
} from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { issueText } from './issueText';
import { previewColumns, rowsToImport, type PreviewColumn } from './onlineImportState';
import type {
  ImportIssue,
  ImportRowOutcome,
  OnlineImportMapping,
  OnlineImportPreview,
  OnlineImportPreviewRow,
  OnlineImportTarget,
} from './onlineImportService';

type PreviewFilter = 'all' | ImportRowOutcome;

const OUTCOME_COLOR: Record<ImportRowOutcome, 'success' | 'info' | 'error'> = {
  Create: 'success',
  Update: 'info',
  Error: 'error',
};

const ROW_FIELD = '__rowNumber';
const OUTCOME_FIELD = '__outcome';

/** The row's reasons take the height they need. */
const autoHeight = () => 'auto' as const;

type LabelOf = (field?: string | null) => string;

interface OnlineImportPreviewStepProps {
  preview: OnlineImportPreview;
  target: OnlineImportTarget;
  /** The mappings the preview ran with; its columns are the fields they fill. */
  mappings: OnlineImportMapping[];
  /** The list's route; a record opens at `${basePath}/${id}`. */
  basePath?: string;
}

/**
 * Step 2: what importing the file would do, row by row, from a dry run that wrote nothing
 * (integration_architecture.md, Online import: preview) — which rows create a record, which update
 * one, why the others cannot go in, and every mapped field as the import reads it, beside the
 * file's value where the column's transforms changed it. The list renders only the rows and
 * columns in view, so a file at the row cap scrolls as one of ten rows does.
 */
export function OnlineImportPreviewStep({ preview, target, mappings, basePath }: OnlineImportPreviewStepProps) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<PreviewFilter>(preview.errors > 0 ? 'Error' : 'all');
  const rows = useMemo(
    () => (filter === 'all' ? preview.rows : preview.rows.filter((row) => row.outcome === filter)),
    [preview.rows, filter],
  );
  const columns = useMemo(() => previewColumns(target, mappings), [target, mappings]);
  const labelOf = useCallback<LabelOf>(
    (field) => target.fields.find((f) => f.name === field)?.label ?? field ?? '',
    [target.fields],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Alert severity={rowsToImport(preview) > 0 ? 'info' : 'error'}>
        {t('onlineImport.preview.summary', {
          defaultValue: '{{creates}} new, {{updates}} to update, {{errors}} with errors.',
          creates: preview.creates, updates: preview.updates, errors: preview.errors,
        })}
        {' '}
        {t('onlineImport.preview.nothingSaved', { defaultValue: 'Nothing has been saved yet.' })}
      </Alert>
      {!target.canCreate && (
        <Alert severity="info">{t('onlineImport.updateOnly', { defaultValue: 'You may only update existing records; rows that would create one fail.' })}</Alert>
      )}
      {!target.canUpdate && (
        <Alert severity="info">{t('onlineImport.createOnly', { defaultValue: 'You may only create records; rows that match an existing one fail.' })}</Alert>
      )}
      <ToggleButtonGroup
        size="small"
        exclusive
        value={filter}
        onChange={(_e, value: PreviewFilter | null) => value && setFilter(value)}
        sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { textTransform: 'none', py: 0.25 } }}
      >
        <ToggleButton value="all">{t('onlineImport.preview.filter.all', { defaultValue: 'All ({{count}})', count: preview.total })}</ToggleButton>
        <ToggleButton value="Update">{t('onlineImport.preview.filter.updates', { defaultValue: 'To update ({{count}})', count: preview.updates })}</ToggleButton>
        <ToggleButton value="Create">{t('onlineImport.preview.filter.creates', { defaultValue: 'New ({{count}})', count: preview.creates })}</ToggleButton>
        <ToggleButton value="Error">{t('onlineImport.preview.filter.errors', { defaultValue: 'Errors ({{count}})', count: preview.errors })}</ToggleButton>
      </ToggleButtonGroup>
      <PreviewRows rows={rows} columns={columns} basePath={basePath} labelOf={labelOf} />
      <Typography variant="caption" color="text.secondary">
        {t('onlineImport.preview.legend', {
          defaultValue: 'A struck-through value is the file’s, before the column’s transforms; an italic one is a default.',
        })}
        {' '}
        {t('onlineImport.preview.rulesHint', {
          defaultValue: 'Rules applied when a record is saved are checked when you import. Rows with errors are skipped.',
        })}
      </Typography>
    </Box>
  );
}

interface PreviewRowsProps {
  rows: OnlineImportPreviewRow[];
  columns: PreviewColumn[];
  basePath?: string;
  labelOf: LabelOf;
}

/** One line per row, a column per mapped field; a row's reasons below it, always shown. */
function PreviewRows({ rows, columns, basePath, labelOf }: PreviewRowsProps) {
  const { t } = useTranslation();
  const gridColumns = useMemo<GridColDef<OnlineImportPreviewRow>[]>(() => [
    {
      field: ROW_FIELD,
      headerName: t('onlineImport.row', { defaultValue: 'Row' }),
      width: 64,
      valueGetter: (_value, row) => row.rowNumber,
    },
    {
      field: OUTCOME_FIELD,
      headerName: t('onlineImport.preview.outcome', { defaultValue: 'Outcome' }),
      width: 108,
      renderCell: ({ row }) => (
        <Chip size="small" color={OUTCOME_COLOR[row.outcome]} variant={row.outcome === 'Error' ? 'filled' : 'outlined'}
          label={t(`onlineImport.preview.outcomes.${row.outcome}`, { defaultValue: row.outcome })} />
      ),
    },
    ...columns.map((column): GridColDef<OnlineImportPreviewRow> => ({
      field: column.field,
      headerName: column.label,
      description: columnHint(column, t),
      ...(column.isKey ? { width: 160 } : { flex: 1, minWidth: 130 }),
      renderHeader: () => <ColumnHeader column={column} />,
      renderCell: ({ row }) => <PreviewValue row={row} column={column} basePath={basePath} labelOf={labelOf} />,
    })),
  ], [columns, basePath, labelOf, t]);
  const pinned = useMemo(
    () => ({ left: [ROW_FIELD, OUTCOME_FIELD, ...columns.filter((column) => column.isKey).map((column) => column.field)] }),
    [columns],
  );
  const expanded = useMemo(
    () => new Set<GridRowId>(rows.filter((row) => row.issues.length > 0).map((row) => row.rowNumber)),
    [rows],
  );
  const detailPanel = useCallback(
    ({ row }: GridRowParams<OnlineImportPreviewRow>) => (row.issues.length > 0 ? <RowIssues issues={row.issues} labelOf={labelOf} /> : null),
    [labelOf],
  );

  return (
    <Box sx={{ flex: 1, minHeight: 240, display: 'flex', flexDirection: 'column' }}>
      <DataGridPremium
        rows={rows}
        columns={gridColumns}
        getRowId={(row) => row.rowNumber}
        density="compact"
        initialState={{ pinnedColumns: pinned }}
        getDetailPanelContent={detailPanel}
        getDetailPanelHeight={autoHeight}
        detailPanelExpandedRowIds={expanded}
        columnVisibilityModel={{ [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false }}
        hideFooter
        disableColumnMenu
        disableColumnSorting
        disableRowSelectionOnClick
        disablePivoting
        disableAggregation
        localeText={{ noRowsLabel: t('onlineImport.preview.noRows', { defaultValue: 'No rows in this selection.' }) }}
        sx={{
          flex: 1, minHeight: 0, border: 1, borderColor: 'divider',
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          // The reasons stay in view while the columns scroll sideways (RowIssues is sticky).
          '& .MuiDataGrid-detailPanel': { overflow: 'visible' },
        }}
      />
    </Box>
  );
}

/** The field's label, the key marked, and a mark when the column's value goes through transforms. */
function ColumnHeader({ column }: { column: PreviewColumn }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {column.isKey && <Key sx={{ fontSize: 14 }} color="action" />}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 500 }}>{column.label}</Box>
      {column.transformChain && <AutoFixHigh sx={{ fontSize: 14 }} color="action" />}
    </Box>
  );
}

function columnHint(column: PreviewColumn, t: TFunction): string {
  return column.transformChain
    ? t('onlineImport.preview.columnTransformed', {
      defaultValue: 'From the file’s column “{{column}}”, through {{chain}}', column: column.sourceColumn, chain: column.transformChain,
    })
    : t('onlineImport.preview.column', { defaultValue: 'From the file’s column “{{column}}”', column: column.sourceColumn });
}

/** One field of one row, as the preview reports it. */
interface CellState {
  /** What the import reads; absent when it leaves the field as it is. */
  value?: string;
  /** The file's value, when the column's transforms changed it. */
  fileValue?: string;
  isDefault: boolean;
  isCleared: boolean;
  issue?: ImportIssue;
}

function cellState(row: OnlineImportPreviewRow, field: string): CellState {
  return {
    value: row.values[field],
    fileValue: row.fileValues[field],
    isDefault: row.defaulted.includes(field),
    isCleared: row.cleared.includes(field),
    issue: row.issues.find((issue) => issue.severity === 'Error' && issue.field?.toLowerCase() === field.toLowerCase()),
  };
}

/** What the cell's tooltip says: the reason it fails, or where its value comes from. None for a plain value. */
function cellHint(cell: CellState, chain: string | null, t: TFunction, labelOf: LabelOf): string {
  if (cell.issue) return issueText(cell.issue, t, labelOf);
  if (cell.isCleared) return t('onlineImport.preview.clearedHint', { defaultValue: 'The cell is empty and the column clears the field.' });
  if (cell.isDefault) return t('onlineImport.preview.defaultHint', { defaultValue: 'The cell is empty; a new record gets the default.' });
  if (cell.fileValue === undefined) return '';
  return t('onlineImport.preview.transformedHint', {
    defaultValue: 'The file has “{{file}}”; {{chain}} makes it “{{value}}”.', file: cell.fileValue, chain, value: cell.value ?? '',
  });
}

interface PreviewValueProps {
  row: OnlineImportPreviewRow;
  column: PreviewColumn;
  basePath?: string;
  labelOf: LabelOf;
}

/**
 * The value the import reads for the field: italic when it is a default, "cleared" when an empty
 * cell empties the field, after the file's own value struck through when a transform changed it,
 * red when the field fails. The key of a row that updates a record links to it.
 */
function PreviewValue({ row, column, basePath, labelOf }: PreviewValueProps) {
  const { t } = useTranslation();
  const cell = cellState(row, column.field);
  const hint = cellHint(cell, column.transformChain, t, labelOf);
  const link = column.isKey && row.existingId && basePath ? `${basePath}/${row.existingId}` : null;
  return (
    <Tooltip title={hint} disableInteractive>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, height: '100%', overflow: 'hidden', whiteSpace: 'nowrap',
        fontFamily: 'monospace', fontSize: 12, color: cell.issue ? 'error.main' : undefined,
      }}>
        {cell.fileValue !== undefined && (
          <>
            <Box component="span" data-testid="preview-file-value" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
              {cell.fileValue || t('onlineImport.preview.emptyCell', { defaultValue: '(empty)' })}
            </Box>
            <Box component="span" aria-hidden sx={{ color: 'text.secondary' }}>→</Box>
          </>
        )}
        {cell.isCleared ? (
          <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{t('onlineImport.preview.cleared', { defaultValue: 'cleared' })}</Box>
        ) : (
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: cell.isDefault ? 'italic' : undefined }}>
            {link ? <Link href={link} target="_blank" rel="noopener">{cell.value}</Link> : cell.value}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

/** A row's reasons, held at the left edge of the list while its columns scroll sideways. */
function RowIssues({ issues, labelOf }: { issues: ImportIssue[]; labelOf: LabelOf }) {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const { viewportInnerSize } = useGridSelector(apiRef, gridDimensionsSelector);
  return (
    <Box sx={{ position: 'sticky', left: 0, width: viewportInnerSize.width, boxSizing: 'border-box', px: 1.5, pb: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {issues.map((issue, index) => (
        <Typography key={index} variant="body2" color={issue.severity === 'Error' ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'normal' }}>
          {issueText(issue, t, labelOf)}
        </Typography>
      ))}
    </Box>
  );
}
